import React, { useEffect, useState } from 'react';
import { func, object, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import _capitalize from 'lodash/capitalize';
import _get from 'lodash/get';

import ButtonGroup from 'ravenjs/lib/ButtonGroup';
import Typography from 'ravenjs/lib/Typography';
import Col from 'ravenjs/lib/Col';
import { get } from 'ravenjs/utils/lodash';
import Row from 'ravenjs/lib/Row';
import Divider from 'ravenjs/lib/Divider';
import Form from 'ravenjs/lib/Form';

import { MODALS } from 'constants/modals';
import { ACCOUNT_PASSWORD_RESET, LOGIN_MODAL } from 'constants/account';
import MESSAGES from 'constants/messages';
import { DB_FAILURE_ERROR_MESSAGE, ERROR_CODES } from 'constants/login';
import LoginErrors from 'components/LoginFormWrapper/LoginErrors';
import { actions as authActions } from 'modules/auth';
import { selectors as brandingSelectors } from 'modules/branding';
import { actions as uiActions, selectors as uiSelectors } from 'modules/ui';
import { selectors as errorSelectors } from 'modules/errors';
import { extractErrorMessage } from 'utils/errors';
import { sanitizeMarkup } from 'utils/sanitize';
import REGEX_VALIDATE from 'constants/regex';
import _isEmpty from 'lodash/isEmpty';

import Modal from '../Modal';
import loginFormSchema from './LoginFormSchema.json';

const { schema, uiSchema } = loginFormSchema;

const DescriptionStyled = styled(Typography)`
    font-family: Roboto !important;
`;

const ButtonGroupStyled = styled(ButtonGroup)`
    justify-content: ${({ hideCancelButton }) =>
        hideCancelButton ? 'space-around' : 'space-between'};
`;

const Logo = styled.img`
    margin-top: 20px;
    margin-bottom: 0;
    max-height: 70px;
    max-width: 200px;
`;

const Content = styled.div`
    padding: 0 20px 30px 20px;
    width: 100%;
    ${({ theme }) => theme.media.up('md')`
        padding: 0 80px 30px 80px;
    `};
`;

const Wrapper = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 30px auto;
`;

const LoginModal = props => {
    const {
        branding,
        closeModal,
        isLoading,
        loginUser,
        openModal,
        modal,
        internallyLogout,
        loginError,
        setLoginAttemptStatus,
    } = props;
    const formRef = React.useRef();
    const [loginData, setLoginData] = useState({});
    const open = get(modal, 'open', false);
    const redirect = get(modal, 'data.redirectUrl', null);
    const redirectState = get(modal, 'data.redirectState', {});
    const title = get(modal, 'data.title', '');
    const description = get(modal, 'data.description', '');
    const hideCancelButton = get(modal, 'data.hideCancelButton', false);
    const username = get(modal, 'data.username', '');
    const reload = get(modal, 'data.reload', false);

    useEffect(() => {
        if (reload) internallyLogout();
        if (username) setLoginData({ userCreds: { username } });
    }, [internallyLogout, reload, username]);

    const handleLogin = async ({ formData }) => {
        try {
            isLoading(true, MESSAGES.LOADING.LOGGING_IN);
            const { userCreds = {} } = formData;
            await loginUser({
                ...userCreds,
                redirect,
                redirectState,
                reload,
            });
            setLoginAttemptStatus(false);
        } catch (e) {
            const error = extractErrorMessage(e);
            openModal(MODALS.CONFIRM, {
                title: MESSAGES.MODAL.CONFIRM.TITLE.ERROR,
                description: error,
            });
        } finally {
            isLoading(false);
        }
    };

    const transformErrors = errors => {
        return errors.map(error => {
            const name = get(error, 'name');
            const missingProperty = get(error, 'params.missingProperty');
            if (name === 'required') {
                error.message = `${_capitalize(missingProperty)} is a required field.`;
            }
            return error;
        });
    };

    const handleSubmit = () => {
        formRef?.current?.submitButton.click();
    };

    let buttons = [
        {
            onClick: handleSubmit,
            label: 'Sign in',
            color: 'primary',
            type: 'submit',
        },
        {
            onClick() {
                closeModal(MODALS.LOGIN);
            },
            label: 'Cancel',
            color: 'cancelButton',
        },
    ];

    const handleOnChange = ({ formData }) => {
        setLoginData(formData);
    };

    const getErrorDescription = errorCode => {
        let errorDescription = loginError?.description;
        switch (errorCode) {
            case ERROR_CODES.DB_ERROR:
                return DB_FAILURE_ERROR_MESSAGE;
            case ERROR_CODES.LOGIN_ATTEMPT:
                errorDescription = errorDescription.replace(
                    ACCOUNT_PASSWORD_RESET.RESET_YOUR_PASSWORD_1,
                    ACCOUNT_PASSWORD_RESET.RESET_YOUR_PASS_LINK_1
                );
                setLoginAttemptStatus(true);
                return errorDescription;
            case ERROR_CODES.LOGIN_ATTEMPT_EXCEEDED:
                errorDescription = errorDescription.replace(
                    ACCOUNT_PASSWORD_RESET.RESET_YOUR_PASSWORD_2,
                    ACCOUNT_PASSWORD_RESET.RESET_YOUR_PASS_LINK_2
                );
                setLoginAttemptStatus(true);
                return errorDescription;
            default:
                return errorDescription;
        }
    };

    const getLoginError = () => {
        const errorCode = _get(loginError, 'errorCode', '');
        const loginErrorDescription = getErrorDescription(errorCode);
        let errorMap = '';
        if (
            errorCode === ERROR_CODES.LOGIN_ATTEMPT ||
            errorCode === ERROR_CODES.LOGIN_ATTEMPT_EXCEEDED
        ) {
            errorMap = loginErrorDescription.split('.');
        }
        return (
            <>
                {errorMap.length ? (
                    errorMap.map(
                        error =>
                            error !== '' && (
                                <Typography
                                    key={error}
                                    align="center"
                                    style={{ margin: 0 }}
                                    dangerouslySetInnerHTML={sanitizeMarkup(`${error}.`)}
                                />
                            )
                    )
                ) : (
                    <Typography align="center" style={{ margin: 0 }}>
                        {loginErrorDescription}
                    </Typography>
                )}
            </>
        );
    };

    const renderContent = () => {
        const customFormats = {
            username: REGEX_VALIDATE.USERNAME,
        };
        buttons = hideCancelButton ? buttons.filter(({ label }) => label !== 'Cancel') : buttons;
        return (
            <Row margin="auto">
                <Col>
                    <Wrapper>
                        <Logo src={branding.loginLogoUrl} alt="Logo" />
                        <Divider margin="2rem 0" width="80%" />
                        <Content>
                            <Typography
                                gutterBottom="2rem"
                                gutterTop="0"
                                align="center"
                                type="title"
                            >
                                {title || LOGIN_MODAL.TITLE}
                            </Typography>
                            <DescriptionStyled gutterBottom="2rem" gutterTop="0" align="center">
                                {description || LOGIN_MODAL.DESCRIPTION}
                            </DescriptionStyled>
                            {!_isEmpty(loginError) && <LoginErrors>{getLoginError()}</LoginErrors>}
                            <Form
                                ref={formRef}
                                externalSubmission
                                schema={schema}
                                uiSchema={uiSchema}
                                formData={loginData}
                                onChange={handleOnChange}
                                customFormats={customFormats}
                                transformErrors={transformErrors}
                                onSubmit={handleLogin}
                                showErrorList={false}
                            />
                            <ButtonGroupStyled hideCancelButton buttons={buttons} />
                        </Content>
                    </Wrapper>
                </Col>
            </Row>
        );
    };

    return (
        <Modal
            ModalProps={{
                ContentProps: {
                    style: {
                        maxWidth: 550,
                        margin: '1rem',
                    },
                },
            }}
            id={MODALS.LOGIN}
            hideActions
            content={renderContent()}
            open={open}
            backDropProps={{ blur: '4px' }}
        />
    );
};

LoginModal.defaultProps = {
    loginError: null,
};

LoginModal.propTypes = {
    branding: shape({
        loginLogoUrl: string.isRequired,
    }).isRequired,
    closeModal: func.isRequired,
    modal: object.isRequired,
    loginUser: func.isRequired,
    internallyLogout: func.isRequired,
    openModal: func.isRequired,
    isLoading: func.isRequired,
    loginError: object,
    setLoginAttemptStatus: func.isRequired,
};

const mapStateToProps = () =>
    createStructuredSelector({
        branding: brandingSelectors.getConfig,
        modal: uiSelectors.getModalById(MODALS.LOGIN),
        loginError: errorSelectors.getAuthErrors('login'),
    });

const mapDispatchToProps = {
    closeModal: uiActions.closeModal,
    isLoading: uiActions.isLoading,
    loginUser: authActions.loginUser,
    internallyLogout: authActions.internallyLogout,
    openModal: uiActions.openModal,
    setLoginAttemptStatus: authActions.setLoginAttemptStatus,
};

export { LoginModal as LoginModalUnwrapped };
export default connect(mapStateToProps, mapDispatchToProps)(LoginModal);
